import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bell, ArrowRight, X, Globe, Code } from 'lucide-react';

const ComingSoon = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email) {
      setIsSubmitted(true);
      setEmail('');
    }
  };

  return (
    <div style={styles.container}>
      {/* Background Elements */}
      <div style={styles.backgroundImage} />
      <div style={styles.gridOverlay} />
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3]
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        style={styles.ambientGlow}
      />

      <main style={styles.main}>
        {/* Header/Logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          style={styles.header}
        >
          <div style={styles.logoContainer}>
            <img src="/full_logo.png" alt="ValueARC Logo" style={styles.logoImage} />
          </div>
        </motion.div>

        {/* Hero Section */}
        <div style={styles.hero}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div style={styles.badgeContainer}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                style={styles.badge}
              >
                <motion.span
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.5, 1, 0.5]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  style={styles.pulseDot}
                />
                Coming Soon
              </motion.div>
            </div>
            <h1 style={styles.title}>
              The Complete <span style={styles.accent}>M&A Operating System for Founders</span>
            </h1>
            <p style={styles.subtitle}>
              Revolutionizing deal-making with intelligent automation, seamless collaboration,
              and data-driven insights. Built for the modern enterprise.
            </p>
          </motion.div>

          {/* Waitlist Form */}
          {/* <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            style={styles.formContainer}
          >
            {isSubmitted ? (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={styles.successMessage}
              >
                <div style={styles.successIcon}>✓</div>
                <p>You're on the list! We'll be in touch soon.</p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} style={styles.form}>
                <div style={styles.inputWrapper}>
                  <input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    style={styles.input}
                  />
                  <button type="submit" style={styles.button}>
                    <span>Join Waitlist</span>
                    <ArrowRight size={18} />
                  </button>
                </div>
              </form>
            )}
          </motion.div> */}
        </div>

        {/* Features/Trust Section (Subtle) */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.6 }}
          style={styles.footer}
        >
          {/* <div style={styles.socials}>
            <a href="#" style={styles.socialLink}><X size={20} /></a>
            <a href="#" style={styles.socialLink}><Globe size={20} /></a>
            <a href="#" style={styles.socialLink}><Code size={20} /></a>
          </div> */}
          <p style={styles.copyright}>
            © 2025 Digilant Solutions Pvt. Ltd. All rights reserved.
          </p>
        </motion.div>
      </main>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#0B1121',
    color: '#FFFFFF',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    fontFamily: "'Inter', sans-serif",
  },
  backgroundImage: {
    position: 'absolute',
    inset: 0,
    backgroundImage: 'url(/hero-bg.png)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    opacity: 0.15,
    filter: 'grayscale(30%)',
    zIndex: 0,
  },
  gridOverlay: {
    position: 'absolute',
    inset: 0,
    backgroundImage: `
      linear-gradient(rgba(232, 184, 75, 0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(232, 184, 75, 0.03) 1px, transparent 1px)
    `,
    backgroundSize: '80px 80px',
    maskImage: 'radial-gradient(circle at center, black 40%, transparent 90%)',
    WebkitMaskImage: 'radial-gradient(circle at center, black 40%, transparent 90%)',
    zIndex: 1,
  },
  ambientGlow: {
    position: 'absolute',
    width: '600px',
    height: '600px',
    background: 'radial-gradient(circle, rgba(232, 184, 75, 0.15) 0%, transparent 70%)',
    borderRadius: '50%',
    filter: 'blur(80px)',
    top: '20%',
    left: '50%',
    transform: 'translateX(-50%)',
    pointerEvents: 'none',
    zIndex: 0,
  },
  main: {
    position: 'relative',
    zIndex: 1,
    width: '100%',
    maxWidth: '1200px',
    padding: '0 24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  header: {
    marginBottom: '80px',
  },
  logoContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  },
  logoImage: {
    height: 'clamp(60px, 8vw, 96px)',
    width: 'auto',
    maxWidth: '100%',
    objectFit: 'contain',
  },
  hero: {
    maxWidth: '800px',
    marginBottom: '60px',
  },
  badgeContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '32px',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 20px',
    borderRadius: '100px',
    background: 'rgba(232, 184, 75, 0.08)',
    border: '1px solid rgba(232, 184, 75, 0.2)',
    color: '#E8B84B',
    fontSize: '0.75rem',
    fontWeight: 600,
    fontFamily: "'Outfit', sans-serif",
    textTransform: 'uppercase',
    letterSpacing: '0.2em',
    backdropFilter: 'blur(4px)',
    boxShadow: '0 0 20px rgba(232, 184, 75, 0.05)',
  },
  pulseDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: '#E8B84B',
    boxShadow: '0 0 10px #E8B84B',
  },
  title: {
    fontSize: 'clamp(2.5rem, 8vw, 4.5rem)',
    fontWeight: 700,
    fontFamily: "'Outfit', sans-serif",
    lineHeight: 1.1,
    marginBottom: '24px',
    letterSpacing: '-0.04em',
  },
  accent: {
    color: '#E8B84B',
    background: 'linear-gradient(to bottom, #FFF, #E8B84B)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  subtitle: {
    fontSize: 'clamp(1rem, 2vw, 1.25rem)',
    color: 'rgba(255, 255, 255, 0.6)',
    lineHeight: 1.6,
    marginBottom: '48px',
    maxWidth: '600px',
    marginInline: 'auto',
  },
  formContainer: {
    width: '100%',
    maxWidth: '500px',
    marginInline: 'auto',
  },
  form: {
    width: '100%',
  },
  inputWrapper: {
    display: 'flex',
    flexDirection: 'row',
    gap: '12px',
    padding: '8px',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '16px',
    backdropFilter: 'blur(10px)',
    flexWrap: 'wrap',
  },
  input: {
    flex: '1 1 250px',
    padding: '12px 16px',
    background: 'transparent',
    border: 'none',
    color: '#FFFFFF',
    fontSize: '1rem',
    outline: 'none',
  },
  button: {
    padding: '12px 24px',
    background: '#E8B84B',
    color: '#0B1121',
    border: 'none',
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'transform 0.2s, background 0.2s',
    flex: '1 1 auto',
  },
  successMessage: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    color: '#E8B84B',
  },
  successIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: 'rgba(232, 184, 75, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.5rem',
    fontWeight: 700,
  },
  footer: {
    marginTop: 'auto',
    padding: '40px 0',
  },
  socials: {
    display: 'flex',
    justifyContent: 'center',
    gap: '24px',
    marginBottom: '24px',
  },
  socialLink: {
    color: 'rgba(255, 255, 255, 0.4)',
    transition: 'color 0.2s',
    textDecoration: 'none',
  },
  copyright: {
    fontSize: '0.8rem',
    color: 'rgba(255, 255, 255, 0.3)',
    letterSpacing: '0.05em',
  },
};

export default ComingSoon;
